import { Button, Callout, CheckboxBox, FormField } from '@dev-spendesk/grapes';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { type AnalyticalFieldMappingFieldType } from 'src/core/modules/bookkeep';

import { useAccountingSoftwareName } from '../../apis';
import { type AnalyticalFieldExportSettings } from '../../apis/useAnalyticalFieldExportSettings';
import { type SyncMode } from '../../apis/useSetSyncSettings';
import { useFieldTypeDescription } from '../../hooks/useFieldTypeDescription';

type Props = {
  fieldName?: string | undefined;
  externalField: { id: string; name: string } | undefined;
  isSplittable: boolean;
  fieldType: AnalyticalFieldMappingFieldType | undefined;
  variant?: 'primary' | 'secondary';
  value: AnalyticalFieldExportSettings | undefined;
  syncMode?: SyncMode;
  onChange: (settings: AnalyticalFieldExportSettings) => void;
  hasError?: boolean;
};

export const ExportSettings = ({
  fieldName,
  externalField,
  isSplittable,
  fieldType,
  value,
  syncMode,
  hasError,
  onChange,
  variant = 'primary',
}: Props) => {
  const { t } = useTranslation('global');
  const { accountingSoftwareName } = useAccountingSoftwareName();
  const fieldTypeDescription = useFieldTypeDescription(fieldType);
  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    setCanEdit(
      syncMode !== 'none' &&
        externalField?.id !== 'expenseCategory' &&
        !externalField?.id.includes('customSegment'),
    );
  }, [externalField, syncMode]);

  const [sendOnHeader, setSendOnHeader] = useState(
    value?.sendOnHeader ?? false,
  );
  const [sendOnLine, setSendOnLine] = useState(value?.sendOnLine ?? false);

  useEffect(() => {
    if (value) {
      setSendOnHeader(value.sendOnHeader);
      setSendOnLine(value.sendOnLine);
    }
  }, [value]);

  const handleSendOnHeaderChanged = useCallback(
    (isChecked: boolean) => {
      setSendOnHeader(isChecked);
      onChange({
        sendOnHeader: isChecked,
        sendOnLine,
        externalId: value?.externalId ?? '',
      });
    },
    [sendOnLine],
  );

  const handleSendOnLineChanged = useCallback(
    (isChecked: boolean) => {
      setSendOnLine(isChecked);
      onChange({
        sendOnHeader,
        sendOnLine: isChecked,
        externalId: value?.externalId ?? '',
      });
    },
    [sendOnHeader],
  );

  const titleText = useMemo(
    () =>
      t('accounting-integration.exportSettings.title', {
        integration: accountingSoftwareName,
        field: fieldName ?? fieldTypeDescription,
      }),
    [accountingSoftwareName, fieldName],
  );

  return (
    <div>
      <p
        className={classNames(
          'text-content-primary',
          {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'text-center': variant === 'primary',
            'text-left': variant === 'secondary',
          },
          [
            variant === 'primary' ? 'mb-32' : 'mb-8',
            variant === 'primary' ? 'heading-l' : 'title-m',
          ],
        )}
      >
        {titleText}
      </p>

      {syncMode === 'none' && (
        <div className="mt-24">
          <Callout
            variant="warning"
            title={t(
              'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.noSyncValues',
              { integration: accountingSoftwareName },
            )}
          />
        </div>
      )}

      {!canEdit ? (
        <>
          {syncMode !== 'none' && externalField?.id === 'expenseCategory' && (
            <Callout
              variant="info"
              title={t(
                'accounting-integration.exportSettings.expenseCategoryInfo',
                {
                  integration: accountingSoftwareName,
                },
              )}
            />
          )}
          {syncMode !== 'none' &&
            externalField?.id.includes('customSegment') && (
              <Callout
                variant="info"
                title={t(
                  'accounting-integration.exportSettings.customSegmentInfo',
                  {
                    integration: accountingSoftwareName,
                    internalFieldName: fieldName,
                    externalFieldName: externalField?.name,
                  },
                )}
              />
            )}
        </>
      ) : (
        <>
          <div className="mb-4">
            <CheckboxBox
              label={t('accounting-integration.exportSettings.header.option')}
              description={t(
                'accounting-integration.exportSettings.header.description',
                { fieldType: fieldTypeDescription },
              )}
              className={classNames({
                'border-border-alert': hasError,
              })}
              isChecked={sendOnHeader}
              onChange={({ target: { checked } }) =>
                handleSendOnHeaderChanged(checked)
              }
            />
          </div>
          <div className="mt-4 pt-4">
            <FormField
              label={t('accounting-integration.exportSettings.header.option')}
              visuallyHideLabel
              alertMessage={
                hasError
                  ? t('accounting-integration.exportSettings.validationError')
                  : undefined
              }
            >
              <CheckboxBox
                label={t('accounting-integration.exportSettings.line.option')}
                description={t(
                  'accounting-integration.exportSettings.line.description',
                  { fieldType: fieldTypeDescription },
                )}
                isChecked={sendOnLine}
                className={classNames({
                  'border-border-alert': hasError,
                })}
                onChange={({ target: { checked } }) =>
                  handleSendOnLineChanged(checked)
                }
              />
            </FormField>
          </div>
          {isSplittable && sendOnHeader && sendOnLine && (
            <Callout
              className="mt-8 w-[632px]"
              variant="info"
              title={t(
                'accounting-integration.exportSettings.callout.splittableHeaderLine.title',
              )}
            >
              <div>
                {t(
                  'accounting-integration.exportSettings.callout.splittableHeaderLine.description',
                )}
              </div>
              <Button
                variant="secondaryNeutral"
                text={t(
                  'accounting-integration.exportSettings.callout.splittableHeaderLine.helpCenter',
                )}
                label={t(
                  'accounting-integration.exportSettings.callout.splittableHeaderLine.helpCenter',
                )}
                href={t(
                  'accounting-integration.exportSettingsOnboardingGuide.helpCenterLink',
                )}
                component="a"
                target="_blank"
                rel="noopener"
                iconName="arrow-top-right-square"
                className="mt-16"
              />
            </Callout>
          )}
          {isSplittable && sendOnHeader && !sendOnLine && (
            <Callout
              className="mt-8 w-[632px]"
              variant="warning"
              title={t(
                'accounting-integration.exportSettings.callout.splittableHeader.title',
              )}
            >
              <div>
                {t(
                  'accounting-integration.exportSettings.callout.splittableHeader.description',
                )}
              </div>
              <Button
                variant="secondaryNeutral"
                text={t(
                  'accounting-integration.exportSettings.callout.splittableHeader.helpCenter',
                )}
                label={t(
                  'accounting-integration.exportSettings.callout.splittableHeader.helpCenter',
                )}
                href={t(
                  'accounting-integration.exportSettingsOnboardingGuide.helpCenterLink',
                )}
                component="a"
                target="_blank"
                rel="noopener"
                iconName="arrow-top-right-square"
                className="mt-16"
              />
            </Callout>
          )}
        </>
      )}
    </div>
  );
};
